import type { GridColDef } from '@mui/x-data-grid';
import { MAX_TO, type PriceTier } from '@vy/types';
import { formatNumber, purge } from '@vy/utils';
import cn from 'classnames';
import { EditTiersTableDisplayCell } from './edit-tiers-table-column-display-cell';
import { EditTiersTableCell } from './usage-product-variant-table-edit-components/EditTiersTableCell';
import { EditTiersTablePerUnitCellField } from './usage-product-variant-table-edit-components/EditTiersTablePerUnitCellField';
import { EditTiersTablePerUnitColumn } from './usage-product-variant-table-edit-components/EditTiersTablePerUnitColumn';
import type { UsageProductVariantTableRow } from './usage-product-variant-table-edit-types/usage-product-variant-table-row';

export const getEditTierTableColumns = (
  isRevShareProduct: boolean,
  isTieredSubscriptionProduct: boolean,
  isFlatRate: boolean,
  isChunked: boolean,
  setTierValueByIndex: (index: number, value: Partial<PriceTier>) => void,
): GridColDef<UsageProductVariantTableRow>[] => {
  return purge([
    {
      field: 'from',
      headerName: 'Starting at ',
      flex: 1,
      editable: true,
      headerAlign: 'center',
      align: 'center',
      type: 'number',
      sortable: false,
      renderCell: ({ value, row }) => (
        <span
          className={cn('font-sans text-black text-base', {
            'text-gray': row.index === 0,
          })}
        >
          {formatNumber(value)}
        </span>
      ),
    },
    {
      field: 'to',
      headerName: 'Up to and including',
      flex: 1,
      editable: true,
      headerAlign: 'center',
      align: 'center',
      type: 'number',
      sortable: false,
      renderCell: ({ value, row, api }) => {
        const rowCount = api.getRowsCount();
        const isLastElement = row.index === rowCount - 1;
        const isInfinity = value === MAX_TO;

        return (
          <EditTiersTableDisplayCell
            value={value}
            row={row}
            isLastElement={isLastElement}
            isInfinity={isInfinity}
            setTierValueByIndex={setTierValueByIndex}
          />
        );
      },
      renderEditCell: ({ id, field, api, value }) => (
        <EditTiersTableCell id={id} field={field} api={api} value={value} />
      ),
    },
    isFlatRate
      ? {
        field: 'flatRate',
        headerName: isTieredSubscriptionProduct ? 'Fixed Rate' : 'Flat Rate',
        flex: 1,
        editable: true,
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        sortable: false,
        renderCell: ({ row }) => (
          <EditTiersTablePerUnitColumn
            value={row.flatRate}
            row={row}
            isFlatRateColumn
            isTieredSubscriptionProduct={isTieredSubscriptionProduct}
          />
        ),
      }
      : undefined,
    !isTieredSubscriptionProduct
      ? {
        field: 'price',
        headerName: getHeaderName(isRevShareProduct),
        flex: 1,
        editable: true,
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        sortable: false,
        cellClassName: 'px-0',
        renderCell: ({ value, row }) => (
          <EditTiersTablePerUnitColumn value={value} row={row} isRevShareProduct={isRevShareProduct} />
        ),
        renderEditCell: ({ id, field, api, value }) => (
          <EditTiersTablePerUnitCellField id={id} field={field} api={api} value={value} />
        ),
      }
      : undefined,
    isChunked
      ? {
        field: 'chunkSize',
        headerName: 'Bulk size',
        flex: 1,
        editable: true,
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        sortable: false,
        renderCell: ({ value }) => (
          <span className="font-sans text-base text-black">{formatNumber(value)}</span>
        ),
      }
      : undefined,
  ]);
};

function getHeaderName(isRevShareProduct: boolean) {
  if (isRevShareProduct) {
    return 'Rate (%)';
  }

  return 'Unit Price';
}
